
import React, { useState, useEffect, useCallback, useTransition, Suspense, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { useProfile } from '@/hooks/use-profile';
import BulletinForm from '@/components/bulletins/BulletinForm';
import BulletinsGrid from '@/components/bulletins/BulletinsGrid';
import BulletinsHeader from '@/components/bulletins/BulletinsHeader';
import BulletinsTabs from '@/components/bulletins/BulletinsTabs';
import EloPointsAnimation from '@/components/animations/EloPointsAnimation';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useInfiniteBulletins } from '@/hooks/use-infinite-bulletins';
import { useInfiniteRelics } from '@/hooks/use-infinite-relics';
import { BulletinErrorBoundary } from '@/components/bulletins/BulletinErrorBoundary';
import { LoadingFallback } from '@/components/common/LoadingFallback';
import { useIsMobile } from '@/hooks/use-mobile';
import useSlowMode from '@/hooks/use-slow-mode';
import MetaTags from '@/components/meta/MetaTags';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { isSocialMediaBrowser } from '@/utils/browser-detection';
import SocialBrowserBanner from '@/components/social/SocialBrowserBanner';
import BulletinDetailView from '@/components/bulletins/BulletinDetailView';
import BulletinLightbox from '@/components/bulletins/BulletinLightbox';
import FavoriteUsersTab from '@/components/favorites/FavoriteUsersTab';
import { CategoryFilter } from '@/components/bulletins/CategoryFilter';
import { useBulletinCategories } from '@/hooks/use-bulletin-categories';
import { RelicsErrorBoundary } from '@/components/bulletins/RelicsErrorBoundary';
import RelicsGrid from '@/components/bulletins/RelicsGrid';
import RelicInspectView from '@/components/bulletins/relic/RelicInspectView';
import { useSessionHealth } from '@/hooks/use-session-health';
import { getUniqueSessionKey, updateSessionActivity } from '@/utils/device-session';

interface BulletinsPage {
  items: any[];
  nextPage?: number;
}

const Bulletins = () => {
  const {
    user,
    isLoading: isAuthLoading,
    isInitialized
  } = useAuth();
  const {
    toast
  } = useToast();
  const {
    profile
  } = useProfile(user?.id);
  const { data: categories = [] } = useBulletinCategories();
  
  // Extract route params
  const params = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Get current tab and category from URL, with defaults
  const activeTab = (params.tab as 'pour-toi' | 'top-follows' | 'updates') || 'pour-toi';
  const activeCategory = params.category || 'chrono';
  const selectedBulletinId = params.bulletinId || null;
  
  const [formKey, setFormKey] = useState(0);
  const [showEloAnimation, setShowEloAnimation] = useState(false);
  const [eloDiff, setEloDiff] = useState(0);
  const [previousElo, setPreviousElo] = useState<number | null>(null);
  const [realtimeEnabled, setRealtimeEnabled] = useState(true);
  const [activeSubscription, setActiveSubscription] = useState<any>(null);
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const [isPending, startTransition] = useTransition();
  const [isTabChanging, setIsTabChanging] = useState(false);
  const [isInSocialBrowser, setIsInSocialBrowser] = useState(false);
  const [isLoadingTransition, setIsLoadingTransition] = useState(false);
  // Measure content area to respect left navigation on desktop
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentBounds, setContentBounds] = useState<{
    top: number; left: number; width: number; height: number;
  } | null>(null);
  const updateContentBounds = useCallback(() => {
    const el = contentRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setContentBounds({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, []);
  useEffect(() => {
    updateContentBounds();
    window.addEventListener('resize', updateContentBounds);
    return () => window.removeEventListener('resize', updateContentBounds);
  }, [updateContentBounds, isMobile, activeTab, activeCategory]);
  
  const { queuedPostsCount } = useSlowMode();
  const { isHealthy, conflictDetected, forceSessionRefresh } = useSessionHealth();

  // All categories now use regular bulletins (no more relics)
  const isRelicsCategory = false;
  
  // Track previous category to detect real changes
  const previousCategoryRef = useRef(activeCategory);
  const subscriptionRef = useRef<any>(null);
  
  // Only use bulletins hook - no more relics
  const bulletinsQuery = useInfiniteBulletins(
    activeTab === 'top-follows' ? 'pour-toi' : activeTab, 
    activeCategory
  );

  // Use bulletins query for all categories
  const currentQuery = bulletinsQuery;
  
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isError,
    error,
    isLoading: isDataLoading,
    refetch
  } = currentQuery;

  const items = data?.pages.flatMap(page => page.items) ?? [];
  const bulletins = items; // All items are bulletins now

  // Handle default redirects
  useEffect(() => {
    if (!params.tab && !params.category) {
      // If no tab specified, redirect to default
      navigate('/bulletins/pour-toi/chrono', { replace: true });
      return;
    }
    
    if (params.tab === 'pour-toi' && !params.category) {
      // If pour-toi tab but no category, redirect to chrono
      navigate(`/bulletins/pour-toi/chrono`, { replace: true });
      return;
    }
    
    if ((params.tab === 'top-follows' || params.tab === 'updates') && params.category) {
      // If top-follows or updates tab has category, remove category
      navigate(`/bulletins/${params.tab}`, { replace: true });
      return;
    }
  }, [params.tab, params.category, navigate]);

  // Get fetchBulletinById from the bulletins query for backwards compatibility
  const { fetchBulletinById } = bulletinsQuery;
  
  // Removed excessive logging that was causing performance issues

  const isAuthenticating = isAuthLoading || !isInitialized;
  const isContentLoading = isPending || isTabChanging || isDataLoading && !items.length;
  const showLoading = isAuthenticating || isContentLoading;

  // Detect social media browser on mount
  useEffect(() => {
    setIsInSocialBrowser(isSocialMediaBrowser());
  }, []);

  // Handle direct bulletin URL from old query param format
  useEffect(() => {
    const bulletinIdFromQuery = searchParams.get('id');
    if (bulletinIdFromQuery && isInitialized && !isContentLoading) {
      // Redirect to proper URL structure
      navigate(`/bulletins/${activeTab}/${activeCategory}/${bulletinIdFromQuery}`, { replace: true });
    }
  }, [searchParams, activeTab, activeCategory, navigate, isInitialized, isContentLoading]);

  // Load bulletin if selectedBulletinId exists
  useEffect(() => {
    if (selectedBulletinId && isInitialized && !isContentLoading) {
      const bulletinExists = items.some(b => b.id === selectedBulletinId);
      
      if (!bulletinExists) {
        fetchBulletinById(selectedBulletinId)
          .catch(err => {
            console.error('Failed to load bulletin from URL:', err);
            // If bulletin not found, redirect back to feed
            navigate(`/bulletins/${activeTab}/${activeCategory}`, { replace: true });
          });
      }
    }
  }, [selectedBulletinId, isInitialized, isContentLoading, items, fetchBulletinById, navigate, activeTab, activeCategory]);

  useEffect(() => {
    if (isInitialized && user && !isDataLoading) {
      refetch();
    }
  }, [isInitialized, user, refetch, isDataLoading]);

  const fetchBulletinsPage = async (pageParam: number, tab: string): Promise<BulletinsPage> => {
    const startIndex = pageParam * 10;
    try {
      const {
        data,
        error
      } = await supabase.from('bulletins').select(`
          *,
          profiles:profiles(username, profile_image_url, is_admin, is_partner, is_premium, combo_score)
        `).order('created_at', {
        ascending: false
      }).eq('is_update', tab === 'updates').range(startIndex, startIndex + 10 - 1);
      if (error) throw error;
      return {
        items: data || [],
        nextPage: data?.length === 10 ? pageParam + 1 : undefined
      };
    } catch (error) {
      console.error('Error fetching bulletins page:', error);
      throw error;
    }
  };

  const setupRealtimeSubscription = useCallback(() => {
    if (!realtimeEnabled) {
      return null;
    }

    // Create device-specific channel name to prevent conflicts between devices
    const deviceSessionKey = getUniqueSessionKey(user?.id);
    const baseChannelName = `bulletins-${activeTab}-realtime`;
    const channelName = `${baseChannelName}_${deviceSessionKey}`;
    
    // Check if we already have an active subscription for this channel
    if (subscriptionRef.current?.topic === channelName) {
      updateSessionActivity();
      return subscriptionRef.current;
    }
    
    console.log('📡 Creating subscription for:', channelName, '(BULLETINS)');
    
    const channel = supabase.channel(channelName)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'bulletins'
      }, payload => {
        updateSessionActivity();
        
        queryClient.invalidateQueries({
          queryKey: ['infinite-bulletins', activeTab],
          exact: false,
          refetchType: 'active'
        });
      })
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'bulletins'
      }, payload => {
        const bulletinId = payload.new.id;
        updateSessionActivity();
        
        const queryKey = ['infinite-bulletins', activeTab];
        queryClient.setQueryData(
          queryKey,
          (oldData: any) => {
            if (!oldData?.pages?.length) return oldData;
            
            return {
              ...oldData,
              pages: oldData.pages.map((page: any) => ({
                ...page,
                items: page.items.map((item: any) => 
                  item.id === bulletinId ? { ...item, ...payload.new } : item
                )
              }))
            };
          }
        );
      })
      .subscribe();
      
    return channel;
  }, [activeTab, queryClient, realtimeEnabled, isRelicsCategory, user?.id]);

  useEffect(() => {
    const channel = setupRealtimeSubscription();
    
    if (channel && channel !== subscriptionRef.current) {
      // Clean up old subscription if it exists
      if (subscriptionRef.current) {
        console.log('🧹 Cleaning up old subscription:', subscriptionRef.current.topic);
        supabase.removeChannel(subscriptionRef.current);
      }
      
      subscriptionRef.current = channel;
      setActiveSubscription(channel);
    }
    
    return () => {
      // Cleanup handled by category switch effect
    };
  }, [activeTab, setupRealtimeSubscription, isRelicsCategory]);

  useEffect(() => {
    if (isError) {
      console.error('Bulletins fetch error:', error);
      toast({
        title: "Error loading bulletins",
        description: "Please check your connection and try again",
        variant: "destructive"
      });
    }
  }, [isError, error, toast]);

  // Handle category switching with loading state and targeted cleanup
  useEffect(() => {
    const previousCategory = previousCategoryRef.current;
    const categoryChanged = previousCategory !== activeCategory;
    const switchingBetweenRelicsAndBulletins = 
      (previousCategory === 'relics' && activeCategory !== 'relics') ||
      (previousCategory !== 'relics' && activeCategory === 'relics');
    
    if (categoryChanged) {
      console.log('🔄 [CATEGORY SWITCH] From:', previousCategory, 'to:', activeCategory);
      
      setIsTabChanging(true);
      setIsLoadingTransition(true);
      
      // Only clean up subscription when switching between relics and bulletins
      if (switchingBetweenRelicsAndBulletins && subscriptionRef.current) {
        console.log('🧹 [CLEANUP] Removing subscription due to category type change');
        supabase.removeChannel(subscriptionRef.current);
        subscriptionRef.current = null;
        setActiveSubscription(null);
      }
      
      previousCategoryRef.current = activeCategory;
      
      const timer = setTimeout(() => {
        setIsTabChanging(false);
        setIsLoadingTransition(false);
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [activeCategory, isRelicsCategory]);

  const handleTabChange = useCallback((value: string) => {
    console.log('Tab change to:', value);
    
    if (value === 'pour-toi') {
      navigate(`/bulletins/pour-toi/chrono`);
    } else {
      navigate(`/bulletins/${value}`);
    }
  }, [navigate]);

  const handleLike = async (id: string) => {
    try {
      queryClient.setQueryData(
        ['infinite-bulletins', activeTab],
        (oldData: any) => {
          if (!oldData?.pages?.length) return oldData;
          
          const updatedPages = oldData.pages.map((page: any) => ({
            ...page,
            items: page.items.map((item: any) => {
              if (item.id === id) {
                return {
                  ...item,
                  like_count: item.like_count + 1
                };
              }
              return item;
            })
          }));
          
          return {
            ...oldData,
            pages: updatedPages
          };
        }
      );
    } catch (error) {
      console.error('Error updating like count:', error);
      toast({
        title: "Error updating bulletins",
        description: "Please try again",
        variant: "destructive"
      });
    }
  };

  const handleDelete = async () => {
    console.log('🗑️ [BULLETIN DELETE] Refetching after delete:', {
      activeTab,
      timestamp: new Date().toISOString()
    });
    queryClient.invalidateQueries({
      queryKey: ['infinite-bulletins', activeTab],
      exact: false
    });
  };
  
  const handleBulletinClick = (id: string) => {
    console.log('🎯 [BULLETIN CLICK] Navigating to bulletin:', {
      id,
      activeTab,
      activeCategory,
      timestamp: new Date().toISOString()
    });
    
    if (activeTab === 'pour-toi') {
      navigate(`/bulletins/${activeTab}/${activeCategory}/${id}`);
    } else {
      navigate(`/bulletins/${activeTab}/${id}`);
    }
  };

  const handleCloseDetailView = () => {
    console.log('❌ [BULLETIN CLOSE] Navigating back to feed:', {
      previousId: selectedBulletinId,
      activeTab,
      activeCategory,
      timestamp: new Date().toISOString()
    });
    
    if (activeTab === 'pour-toi') {
      navigate(`/bulletins/${activeTab}/${activeCategory}`);
    } else {
      navigate(`/bulletins/${activeTab}`);
    }
  };

  // Find the selected bulletin/relic if any
  const selectedBulletin = selectedBulletinId 
    ? items.find(item => item.id === selectedBulletinId)
    : null;

  if (isAuthenticating) {
    return <LoadingFallback />;
  }

  return (
    <div className="">
      {/* Add MetaTags to ensure consistent social sharing metadata */}
      <MetaTags 
        title="FOMO – Community‑Powered Social Platform with Creator Rewards"
        description="Join a vibrant community, share moments that matter, and earn from your creativity with ad‑revenue share and collectible Drops."
        image="/og-image.png"
        url={window.location.href}
      />
      
      {/* Display social browser banner if detected */}
      {isInSocialBrowser && (
        <SocialBrowserBanner className="mb-4" />
      )}
      
      <div ref={contentRef} className={`container max-w-7xl mx-auto pt-25 px-4 py-[22px] ${isInSocialBrowser ? 'mt-12' : ''}`}>
        <motion.div 
          initial={{ y: -20, opacity: 0 }} 
          animate={{ y: 0, opacity: 1 }} 
          transition={{ delay: 0.2, duration: isMobile ? 0.2 : 0.3 }} 
          className="mx-0"
        >
          <BulletinsHeader activeTab={activeTab} activeCategory={activeCategory} queuedCount={queuedPostsCount} />
        </motion.div>

        <motion.div 
          initial={{ y: -10, opacity: 0 }} 
          animate={{ y: 0, opacity: 1 }} 
          transition={{ delay: 0.3, duration: isMobile ? 0.1 : 0.2 }} 
          className="mb-6"
        >
          <BulletinsTabs 
            activeTab={activeTab} 
            onTabChange={handleTabChange} 
                     activeCategory={activeCategory}
            showTabs={true} 
          />
        </motion.div>
        
        <BulletinErrorBoundary>
          <AnimatePresence mode="wait">
            {selectedBulletin ? (
              <BulletinLightbox
                bulletins={bulletins}
                selectedId={selectedBulletinId!}
                onNavigate={(id) => handleBulletinClick(id)}
                onClose={handleCloseDetailView}
                contentBounds={!isMobile ? contentBounds : null}
              />
            ) : activeTab === 'top-follows' ? (
              <motion.div 
                key={activeTab} 
                initial={{ opacity: 0 }} 
                animate={{ opacity: 1 }} 
                exit={{ opacity: 0 }} 
                transition={{ duration: 0.2 }}
              >
                <FavoriteUsersTab />
              </motion.div>
            ) : (
              <motion.div 
                key={activeTab} 
                initial={{ opacity: 0 }} 
                animate={{ opacity: 1 }} 
                exit={{ opacity: 0 }} 
                transition={{ duration: 0.2 }}
              >
                <motion.div 
                  initial={{ y: 20, opacity: 0 }} 
                  animate={{ y: 0, opacity: 1 }} 
                  transition={{ delay: 0.3, duration: isMobile ? 0.2 : 0.3 }}
                >
                  <BulletinForm 
                    key={formKey} 
                    onSuccess={() => {
                      console.log('Form submission successful, refetching');
                      setTimeout(() => {
                        queryClient.invalidateQueries({
                          queryKey: ['infinite-bulletins', activeTab],
                          exact: false,
                          refetchType: 'active'
                        });
                      }, 300);
                    }} 
                    isUpdateTab={false} 
                  />
                </motion.div>

                {/* Category Filter - only show for pour-toi tab with hashtag categories */}
                {activeTab === 'pour-toi' && categories.length > 0 && (
                  <motion.div 
                    initial={{ y: 10, opacity: 0 }} 
                    animate={{ y: 0, opacity: 1 }} 
                    transition={{ delay: 0.4, duration: 0.3 }}
                    className="mb-6"
                  >
                    <CategoryFilter 
                      categories={categories}
                      activeCategory={activeCategory}
                    />
                  </motion.div>
                )}
                
                <Suspense fallback={<LoadingFallback />}>
                  {isContentLoading ? (
                    <div className="flex justify-center my-12">
                      <LoadingFallback />
                    </div>
                  ) : (
                    <BulletinsGrid 
                      bulletins={bulletins} 
                      onLike={handleLike} 
                      onDelete={handleDelete} 
                      activeTab={activeTab} 
                      onLoadMore={() => {
                        console.log('Loading more bulletins...');
                        fetchNextPage();
                      }} 
                      isLoading={isFetchingNextPage} 
                      hasNextPage={hasNextPage || false} 
                      onBulletinClick={handleBulletinClick}
                      selectedBulletinId={selectedBulletinId || undefined}
                    />
                  )}
                </Suspense>
              </motion.div>
            )}
          </AnimatePresence>
        </BulletinErrorBoundary>

        <EloPointsAnimation points={eloDiff} show={showEloAnimation} />
      </div>
    </div>
  );
};

export default Bulletins;
